import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { announce } from '../state/announcer.ts';
import { Button, type ButtonProps } from './Button.tsx';
import styles from './CopyButton.module.css';

interface CopyButtonProps {
  /** The text written to the clipboard. */
  value: string;
  /** What is copied, for the accessible name and announcement, e.g. "target". */
  what: string;
  /** Visible idle label. */
  label?: string;
  /** Selects this element's text when the clipboard is unavailable. */
  selectTarget?: () => HTMLElement | null;
  size?: ButtonProps['size'];
  block?: boolean;
  showIcon?: boolean;
}

type CopyState = 'idle' | 'copied' | 'manual';

async function writeClipboard(value: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function selectContents(el: HTMLElement | null) {
  if (!el) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

// Copy with visible, announced feedback (DOMAIN_AUTHORIZATION_SPEC.md §5.4). The
// button keeps its width while its label changes.
export function CopyButton({
  value,
  what,
  label = 'Copy',
  selectTarget,
  size = 'small',
  block = false,
  showIcon = false,
}: CopyButtonProps) {
  const [state, setState] = useState<CopyState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const capitalized = what.charAt(0).toUpperCase() + what.slice(1);

  const onPress = async () => {
    clearTimeout(timer.current);
    if (await writeClipboard(value)) {
      setState('copied');
      announce(`${capitalized} copied`);
      timer.current = setTimeout(() => setState('idle'), 2000);
    } else {
      setState('manual');
      selectContents(selectTarget?.() ?? null);
      announce('Press Ctrl+C, or ⌘C on Mac, to copy.');
    }
  };

  const labels: Record<CopyState, string> = {
    idle: label,
    copied: 'Copied',
    manual: 'Select and copy',
  };

  return (
    <span className={[styles.wrap, block ? styles.block : ''].join(' ')}>
      <Button size={size} block={block} onPress={onPress} aria-label={`Copy ${what}`}>
        <span className={styles.stack}>
          {/* Both labels occupy the same cell so the width never jumps. */}
          {(['idle', 'copied'] as const).map((k) => (
            <span
              key={k}
              className={styles.label}
              data-visible={state === k || (k === 'idle' && state === 'manual') || undefined}
            >
              {k === 'copied' ? (
                <Check size={16} aria-hidden="true" />
              ) : (
                showIcon && <Copy size={16} aria-hidden="true" />
              )}
              {k === 'idle' && state === 'manual' ? labels.manual : labels[k]}
            </span>
          ))}
        </span>
      </Button>
      {state === 'manual' && <span className={styles.hint}>Press Ctrl+C (⌘C on Mac) to copy.</span>}
    </span>
  );
}
